import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  Link,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';
import {
  PersonOutline as PersonOutlineIcon,
  FavoriteBorder as FavoriteIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

const SignIn = () => {
  const [tab, setTab] = useState(0); // 0: Practitioner, 1: Patient
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setFormData({ email: '', password: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission behavior - MUST be first
    if (e) {
      if (e.preventDefault) {
        e.preventDefault();
      }
      if (e.stopPropagation) {
        e.stopPropagation();
      }
      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
      if (e.persist) {
        e.persist(); // For React synthetic events
      }
      
      // Prevent any default browser behavior
      if (e.defaultPrevented === false) {
        e.preventDefault();
      }
    }
    
    // Client-side validation
    if (!formData.email || !formData.password) {
      try {
        await SwalWithA11yFix({
          icon: 'warning',
          title: 'Missing Information',
          text: 'Please enter both email and password',
          confirmButtonColor: '#1976d2',
        });
      } catch (swalError) {
        console.error('SweetAlert error:', swalError);
      }
      return;
    }

    setLoading(true);

    try {
      // Send the selected role (tab === 0 means Practitioner, tab === 1 means Patient)
      const selectedRole = tab === 0 ? 'practitioner' : 'patient';
      const response = await api.post('/auth/login', {
        ...formData,
        role: selectedRole
      });
      
      // Check if response is valid
      if (!response || !response.data || !response.data.user) {
        throw new Error('Invalid response from server');
      }
      
      const role = response.data.user.role;
      
      // Verify the returned role matches the selected tab
      if ((tab === 0 && role !== 'practitioner') || (tab === 1 && role !== 'patient')) {
        setLoading(false);
        await SwalWithA11yFix({
          icon: 'error',
          title: 'Invalid Role',
          text: `This account is for ${role}s only. Please login using the correct tab.`,
          confirmButtonColor: '#1976d2',
          confirmButtonText: 'OK',
        });
        return;
      }
      
      // Only proceed if we have valid token and user
      if (!response.data.token) {
        throw new Error('No authentication token received');
      }
      
      login(response.data.token, response.data.user);
      
      // Show success message
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Welcome!',
        text: `Successfully signed in as ${tab === 0 ? 'Practitioner' : 'Patient'}`,
        timer: 2000,
        showConfirmButton: false,
      });
      
      // Redirect based on role - only on success
      if (role === 'practitioner') {
        navigate('/practitioner/dashboard');
      } else if (role === 'patient') {
        navigate('/patient/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      // CRITICAL: Set loading to false FIRST - before anything else
      setLoading(false);
      
      // Extract error message BEFORE showing alert
      let errorMsg = 'Login failed. Please check your credentials and try again.';
      
      if (err.response) {
        // Server responded with error
        errorMsg = err.response.data?.message || 
                   (Array.isArray(err.response.data?.errors) 
                     ? err.response.data.errors.join(', ') 
                     : 'Invalid credentials. Please try again.');
      } else if (err.message) {
        // Network or other error
        errorMsg = err.message;
      }
      
      // Show error using SweetAlert - MUST await to block execution
      // Use setTimeout to ensure it runs after any pending navigation
      setTimeout(async () => {
        try {
          await SwalWithA11yFix({
            icon: 'error',
            title: 'Login Failed',
            html: `<div style="text-align: center; padding: 10px;">${errorMsg}</div>`,
            confirmButtonColor: '#1976d2',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
            allowEscapeKey: true,
            allowEnterKey: false,
            showCloseButton: true,
            focusConfirm: true,
            backdrop: true,
            backdropClass: 'swal2-backdrop-show',
            didOpen: () => {
              // Prevent any form interactions while alert is open
              const forms = document.querySelectorAll('form');
              forms.forEach(form => {
                form.style.pointerEvents = 'none';
                form.addEventListener('submit', (e) => e.preventDefault(), true);
              });
            },
            didClose: () => {
              // Re-enable form interactions after alert closes
              const forms = document.querySelectorAll('form');
              forms.forEach(form => {
                form.style.pointerEvents = 'auto';
              });
            },
          });
        } catch (swalError) {
          console.error('SweetAlert error:', swalError);
          // Fallback: show browser alert if SweetAlert fails
          alert(errorMsg);
        }
      }, 0);
      
      // CRITICAL: Return immediately - don't continue execution
      return false;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'white',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ color: '#1976d2' }}>
              AnalytikalDNA
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Sign in to your account
            </Typography>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            centered
            sx={{
              mb: 4,
              '& .MuiTab-root': {
                minWidth: 150,
                textTransform: 'none',
                fontSize: '1rem',
              },
              '& .Mui-selected': {
                color: '#1976d2',
                fontWeight: 'bold',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3,
              },
            }}
          >
            <Tab
              icon={<PersonOutlineIcon />}
              iconPosition="start"
              label="Practitioner"
              sx={{ color: tab === 0 ? '#1976d2' : 'text.secondary' }}
            />
            <Tab
              icon={<FavoriteIcon />}
              iconPosition="start"
              label="Patient"
              sx={{ color: tab === 1 ? '#1976d2' : 'text.secondary' }}
            />
          </Tabs>


          {/* Form */}
          <Box
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation?.();
                // Prevent any form submission
                const target = e.target;
                if (target && target.form) {
                  e.preventDefault();
                }
                handleSubmit(e);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Email Address <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation?.();
                    // Prevent any form submission
                    const target = e.target;
                    if (target && target.closest('form')) {
                      e.preventDefault();
                    }
                    handleSubmit(e);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                placeholder="Enter your email"
                autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Password <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Link
                  component={RouterLink}
                  to={`/signup?tab=${tab === 0 ? 'practitioner' : 'patient'}`}
                  sx={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Reset Password
                </Link>
              </Box>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation?.();
                    // Prevent any form submission
                    const target = e.target;
                    if (target && target.closest('form')) {
                      e.preventDefault();
                    }
                    handleSubmit(e);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                placeholder="Enter your password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>

            <Button
              type="button"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              onClick={async (e) => {
                // Prevent all default behavior
                e.preventDefault();
                e.stopPropagation();
                if (e.stopImmediatePropagation) {
                  e.stopImmediatePropagation();
                }
                if (e.persist) {
                  e.persist();
                }
                
                // Prevent form submission if button is in a form
                const button = e.currentTarget;
                if (button && button.form) {
                  button.form.preventDefault?.();
                }
                
                // Call handleSubmit
                await handleSubmit(e);
              }}
              sx={{
                py: 1.5,
                mb: 2,
                backgroundColor: '#1976d2',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {loading ? 'Signing in...' : `Sign in as ${tab === 0 ? 'Practitioner' : 'Patient'}`}
            </Button>
          </Box>

          {/* Registration is disabled for now (no sign up link) */}

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2 }}>
              Other Sign In Options
            </Typography>
          </Divider>

          {/* Enterprise Sign In */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              py: 1.5,
              borderColor: '#1976d2',
              color: '#1976d2',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
            onClick={() => navigate('/admin-login')}
          >
            Sign In With Enterprise Account
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignIn;
